import {
  CheckOutlined,
  CloseOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { Button, Form, Popover, Space, Switch } from 'antd';
import React from 'react';
import { Param as ParamType } from '../../cliDataSchema';
import InlineMarkdown from '../InlineMarkdown';

type Props = {
  param: ParamType;
  children: React.ReactNode;
  disabled: boolean;
  setDisabled: (disabled: boolean) => void;
  visibleParams: ParamType[];
};

const ParamFormItem: React.FC<Props> = ({
  param,
  children,
  disabled,
  setDisabled,
  visibleParams,
}) => {
  const hasDependencies =
    Array.isArray(param.osep_generator_ui_required_if_one_of) &&
    param.osep_generator_ui_required_if_one_of.length > 0;
  const form = Form.useFormInstance();

  const required = param.required || param.osep_generator_ui_recommended;
  const requiredMessage =
    param.osep_generator_ui_recommended && !param.required
      ? 'This field is recommended. Deactivate it if you do not want to use it.'
      : 'This field is required.';

  const handleChangeDisabled = (checked: boolean) => {
    if (!checked) {
      form.setFieldsValue({ [param.name]: undefined });
      form.validateFields([param.name]);
    }
    setDisabled(!checked);
  };

  return (
    <Form.Item
      key={param.name}
      name={param.name}
      label={
        <Space size={0}>
          {param.osep_generator_ui_display_name}
          {param.help && (
            <Popover
              content={<InlineMarkdown>{param.help}</InlineMarkdown>}
              title={param.osep_generator_ui_display_name}
              trigger="click"
            >
              <Button
                size="middle"
                icon={<QuestionCircleOutlined />}
                type="text"
              />
            </Popover>
          )}
          {param.osep_generator_ui_recommended && (
            <Switch
              checkedChildren={<CheckOutlined />}
              unCheckedChildren={<CloseOutlined />}
              onChange={handleChangeDisabled}
              defaultChecked={!disabled}
            />
          )}
        </Space>
      }
      required={required}
      dependencies={
        hasDependencies ? param.osep_generator_ui_required_if_one_of : []
      }
      rules={[
        { required: !disabled && required, message: requiredMessage },
        ({ getFieldValue }) => ({
          validator(_, value) {
            if (hasDependencies) {
              const filledDependentFields =
                param.osep_generator_ui_required_if_one_of.filter(
                  (dependentField) => getFieldValue(dependentField),
                );

              if (filledDependentFields.length > 0 && !value) {
                const fieldName = param.osep_generator_ui_display_name;

                const filledDependentField = filledDependentFields
                  .map((dependentField) => {
                    const dependentParam = visibleParams.find(
                      (param) => param.name === dependentField,
                    );
                    return dependentParam
                      ? `'${dependentParam.osep_generator_ui_display_name}'`
                      : `'${dependentField}'`;
                  })
                  .join(' or ');

                return Promise.reject(
                  new Error(
                    `'${fieldName}' can't be empty if ${filledDependentField} is filled!`,
                  ),
                );
              }
            }

            return Promise.resolve();
          },
        }),
      ]}
    >
      {children}
    </Form.Item>
  );
};

export default ParamFormItem;
